import { useRef } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { askToMoveHint, doneDetailBody } from "../lib/calendarCopy";
import { monthDayLabel } from "../lib/recordsCopy";
import type { ScheduleOccurrence } from "../types";

export interface CalendarDetailPopoverProps {
  occurrence: ScheduleOccurrence | null;
  /** True for a member (or anyone else who can't act on this occurrence)
   * — gates §2.10a's "Ask `<name>` to move this" hint. */
  isReadOnlyViewer: boolean;
  /** Set only for a writer viewing a SKIPPED ghost chip (§2.5: "tap ->
   * read-only detail + Undo affordance if writer role") — every other
   * combination omits this, leaving the popover purely informational. */
  onUndo?: () => void;
  onClose: () => void;
}

/**
 * §2.10/§2.10a — the read-only chip detail. A `.is-done` chip is NEVER a
 * reschedule-sheet trigger (§2.10); a member's tap on any non-done chip
 * resolves here too (§2.11), with the R-B0b-3 "ask someone" hint added for
 * live/skipped/moved chips only, never done chips.
 *
 * **Implementation note (flagged in the PR):** the spec's own mock shows
 * this as a small anchored popover reusing "the same overflow-expansion
 * pattern as §2.3's '+2 more.'" This build instead reuses the app's
 * existing `.assign-sheet` bottom-sheet chrome (same visual language,
 * same `useModalA11y` focus-trap/Escape/restore behavior every other
 * sheet in this app already has) rather than building new anchored/
 * viewport-aware popover-positioning logic from scratch for one component
 * — functionally equivalent (tap to open; Close/Escape/backdrop to
 * dismiss), simpler, and more consistent with this codebase's established
 * overlay pattern.
 */
export function CalendarDetailPopover({
  occurrence,
  isReadOnlyViewer,
  onUndo,
  onClose,
}: CalendarDetailPopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const open = occurrence !== null;
  // Sprint-011 C1 (TD-012, SA plan §3 clause 3) — backdrop + Close route
  // through the hook's returned `close`, not `onClose` directly.
  const { close } = useModalA11y(containerRef, { active: open, onClose });

  if (!occurrence) return null;

  const isDone = occurrence.status === "done";
  const showHint = isReadOnlyViewer && !isDone;

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open calendar-detail-popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-detail-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="calendar-detail-title">
          {occurrence.title}
        </h2>
        <p class="calendar-detail-body">
          {isDone ? doneDetailBody(monthDayLabel(occurrence.date)) : (occurrence.phrase ?? "")}
        </p>
        {showHint ? <p class="calendar-detail-hint">{askToMoveHint()}</p> : null}
        {onUndo ? (
          <button type="button" class="primary-btn" onClick={onUndo}>
            Undo
          </button>
        ) : null}
        <button type="button" class="secondary-btn" onClick={close}>
          Close
        </button>
      </div>
    </>
  );
}
